import { useProductsWithRelations } from "@/services/product-service";
import { useMemo } from "react";
import { POS_CATEGORIES } from "../constants/pos-categories";
import { PosProductCard } from "./pos-product-card";
import type { TProductWithRelation } from "@/types/product-type";
import { Package } from "lucide-react";
import { usePosContext } from "../context/pos-context";

function filterByCategory(
  products: TProductWithRelation[],
  categoryId: string
): TProductWithRelation[] {
  if (categoryId === "all") return products;

  const category = POS_CATEGORIES.find((c) => c.id === categoryId);
  if (!category || category.searchTerms.length === 0) return products;

  const terms = category.searchTerms.map((t) => t.toLowerCase());
  return products.filter((p) => {
    const searchable = [
      p.product.name,
      p.product.description,
      ...p.productVariants.map((v) => v.name),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return terms.some((term) => searchable.includes(term));
  });
}

function filterBySearch(
  products: TProductWithRelation[],
  searchTerm: string
): TProductWithRelation[] {
  const trimmed = searchTerm.trim();
  if (!trimmed) return products;
  const terms = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  return products.filter((p) => {
    const searchable = [
      p.product.name,
      p.product.description,
      p.product.code,
      ...p.productVariants.map((v) => v.name),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return terms.every((term) => searchable.includes(term));
  });
}

function ProductCardSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden bg-white border border-gray-100 animate-pulse">
      <div className="aspect-[4/3] bg-gray-200" />
      <div className="p-3 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
  );
}

export function PosProductGrid() {
  const { activeCategoryId, searchTerm } = usePosContext();
  const { data: products, isLoading } = useProductsWithRelations();
  const productList = products ?? [];

  const filteredProducts = useMemo(() => {
    const byCategory = filterByCategory(productList, activeCategoryId);
    return filterBySearch(byCategory, searchTerm);
  }, [productList, activeCategoryId, searchTerm]);

  const activeCategory = POS_CATEGORIES.find((c) => c.id === activeCategoryId);
  const categoryLabel = activeCategory?.name ?? "All Menu";

  if (isLoading) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {categoryLabel}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-foreground mb-4">
        {categoryLabel}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredProducts.map((pwr) => (
          <PosProductCard key={pwr.product.id} productWithRelation={pwr} />
        ))}
      </div>
      {filteredProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="rounded-full bg-gray-100 p-6 mb-4">
            <Package className="h-12 w-12 text-gray-400" />
          </div>
          <p className="text-muted-foreground font-medium">No products found</p>
          <p className="text-sm text-muted-foreground mt-1">
            {searchTerm
              ? "Try a different search term"
              : "No products in this category yet"}
          </p>
        </div>
      )}
    </div>
  );
}
